import { useEffect } from "react";

/**
 * Kendo's MultiSelect has accessible-name/state gaps with no supported
 * prop-based fix:
 *  1. The selected-tags chip list renders as its own role="listbox" element
 *     (id `tagslist-${id}`) that gets real keyboard focus before the text
 *     input does, but Kendo exposes no prop to name it - so it announces as
 *     a bare, unnamed "list", and a screen reader user landing on the field
 *     has no idea what's already selected.
 *  2. Each individual chip (role="option") gets no accessible name of its
 *     own either: its visible text lives in a *nested* span that carries
 *     its own aria-label, and Kendo's Chip component doesn't forward a
 *     generic aria-label prop to the outer chip element - confirmed via
 *     Kendo's own Chip.mjs source, and via a screen reader announcing
 *     "blank" when hovering a selected chip.
 * Both are fixed the same way: read what's already correctly rendered in
 * the DOM (each chip's own visible text) and copy it into the right
 * aria-label, once mounted/updated. Reading from the DOM rather than from
 * the field's own data avoids needing to know each caller's field-name
 * shape (Option.Text, LanguageOption.Text, ...).
 *
 *  3. Arrow-key navigation between chips (Left/Right from the input, once
 *     it's empty) moves Kendo's own internal "current chip" state and
 *     repaints it via the .k-focus class, but never tells assistive tech
 *     anything: real DOM focus never leaves the input, and Kendo only ever
 *     renders aria-activedescendant on the input while the popup is open -
 *     with the popup closed (the normal case here), it's silently omitted,
 *     confirmed via MultiSelect.mjs's own source. Fixed by watching for
 *     that .k-focus class to move (via MutationObserver, since nothing
 *     else signals the change) and mirroring it onto aria-activedescendant
 *     on the input ourselves - the same technique Kendo's own docs
 *     describe as the intended behavior, just not fully wired in this
 *     version. Safe to set outside of React: Kendo's own JSX only ever
 *     assigns that same attribute while the popup is open, so as long as
 *     the popup stays closed there's nothing to fight over with React's
 *     reconciliation.
 *
 *     aria-activedescendant needs something to actually point at, which
 *     took an extra layer to track down: TagList.mjs does try to give each
 *     chip an id (`tag-${guid}-${text}`), but Kendo's own Chip.mjs render
 *     function ignores that prop and hardcodes the DOM id to `e.value`
 *     instead - and TagList never passes a `value` prop when rendering a
 *     MultiSelect's tags, so every chip ends up with no id attribute at
 *     all. Confirmed directly in both files' source. Chips are given a
 *     real id here (in the same per-chip pass as the aria-label above) so
 *     there's something valid for aria-activedescendant to reference.
 */
export function useMultiSelectChipListLabel(
  id: string | undefined,
  ariaLabel: string | undefined,
  ...deps: unknown[]
) {
  useEffect(() => {
    if (!id) return;
    const tagList = document.getElementById(`tagslist-${id}`);
    if (!tagList) return;

    const chips = Array.from(
      tagList.querySelectorAll<HTMLElement>(".k-chip")
    );
    const chipTexts = chips.map(
      (chip) => chip.querySelector(".k-chip-label")?.textContent?.trim() ?? ""
    );

    chips.forEach((chip, i) => {
      if (chipTexts[i]) chip.setAttribute("aria-label", chipTexts[i]);
      if (!chip.id) chip.id = `${id}-chip-${i}`;
    });

    if (ariaLabel) {
      const selected = chipTexts.filter(Boolean);
      tagList.setAttribute(
        "aria-label",
        selected.length > 0
          ? `${ariaLabel}, selected: ${selected.join(", ")}`
          : ariaLabel
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ariaLabel, ...deps]);

  useEffect(() => {
    if (!id) return;
    const input = document.getElementById(id);
    const tagList = document.getElementById(`tagslist-${id}`);
    if (!input || !tagList) return;

    const syncActiveDescendant = () => {
      const focusedChip = tagList.querySelector<HTMLElement>(
        ".k-chip.k-focus"
      );
      if (focusedChip?.id) {
        input.setAttribute("aria-activedescendant", focusedChip.id);
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    };

    syncActiveDescendant();
    const observer = new MutationObserver(syncActiveDescendant);
    observer.observe(tagList, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });
    return () => observer.disconnect();
  }, [id]);
}
